import { ReactNode } from 'react';
import { Thead, Tbody, Tr, Th as CKTh, Td } from '@chakra-ui/react';
import { Colors, Text } from '@mercator-gcp/grout';
import { useNavigate } from 'react-router-dom';
import { CompanyChip } from 'components/elements/Chips/CompanyChip';
import Table from 'components/layout/Table';
import { LandTitle } from 'types/LandTitle';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';
import { formatDate } from 'utils/date';
import { capitalizeWords } from 'utils/format';

type Props = {
  landTitleHistory: LandTitle[] | undefined;
};

const tableStyle = {
  maxHeight: '300px',
  overflowY: 'auto',
};

const stickyHeaderStyle = {
  '>tr>th:first-of-type': {
    position: 'sticky',
    left: '0',
    zIndex: Z_INDEX_DICT.LEAD_TABLE_LEFT_COLUMN_HEADER,
  },
  '>tr>th': {
    position: 'sticky',
    top: '0',
    backgroundColor: Colors.White,
    zIndex: Z_INDEX_DICT.LEAD_TABLE_HEADER,
  },
};

const updatedDateStyle = {
  textTransform: 'uppercase',
};

const tableItem = (isClickable: boolean) => ({
  _hover: {
    cursor: isClickable ? 'pointer' : 'default',
    background: 'brand.grey-lighter',
  },
});

const Th = ({ children }: { children: ReactNode }) => {
  return (
    <CKTh>
      <Text.SmallLabel color={Colors.ArmyGreen500}>{children}</Text.SmallLabel>
    </CKTh>
  );
};

const TitleSummaryTable = ({ landTitleHistory }: Props) => {
  const navigate = useNavigate();

  const handleActivityClick = (hasPermitNum: boolean, uuid: string) => {
    const activityDocType = 'Land Title Change';
    const search = `?${activityDocType?.split(' ').join('-').toLowerCase()}=${uuid}`;

    if (hasPermitNum) {
      navigate(search);
    }
  };

  return (
    <Table sx={tableStyle}>
      <Thead sx={stickyHeaderStyle}>
        <Tr>
          <Th>Unit Number</Th>
          <Th>Latest Title Change</Th>
          <Th>Land Owner(s)</Th>
        </Tr>
      </Thead>
      <Tbody>
        {landTitleHistory?.map((landTitle) => {
          return (
            <Tr
              key={landTitle.document_id}
              onClick={() => handleActivityClick(!!landTitle?.uuid, landTitle.uuid)}
              sx={tableItem(!!landTitle?.uuid)}
            >
              <Td>
                <Text sx={updatedDateStyle}>{landTitle.parcelnum}</Text>
              </Td>
              <Td>
                <Text.SmallBold sx={updatedDateStyle}>
                  {formatDate(landTitle.issueddate)}
                </Text.SmallBold>
              </Td>
              <Td>
                {landTitle.companies.map((company) => {
                  const companyName = capitalizeWords(company.name);

                  return (
                    <CompanyChip
                      key={company.uuid}
                      name={companyName}
                      id={company.uuid}
                      onClick={() => navigate(`/companies/${company.uuid}`)}
                    />
                  );
                })}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};

export default TitleSummaryTable;
